import React, { useState, useEffect } from 'react';

interface ApiKeyConfigProps {
  isOpen: boolean;
  apiKey?: string;
  securityKey?: string;
  onClose: () => void;
  onSave: (apiKey: string, securityKey: string) => Promise<void>;
  loading?: boolean;
}

const ApiKeyConfig: React.FC<ApiKeyConfigProps> = ({
  isOpen,
  apiKey: initialApiKey = '',
  securityKey: initialSecurityKey = '',
  onClose,
  onSave,
  loading = false
}) => {
  const [apiKey, setApiKey] = useState<string>('');
  const [securityKey, setSecurityKey] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setApiKey(initialApiKey || '');
      setSecurityKey(initialSecurityKey || '');
    }
  }, [isOpen, initialApiKey, initialSecurityKey]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!apiKey.trim() || !securityKey.trim()) {
      alert('请填写完整的API Key和安全密钥');
      return;
    }

    await onSave(apiKey.trim(), securityKey.trim());
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="modal-content config-modal">
        <div className="modal-header">
          <h3 className="modal-title">配置高德地图API</h3>
          {!loading && (
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">
                API Key <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="请输入高德地图 Web 服务 Key"
                disabled={loading}
                required
              />
              <div className="form-hint">
                在{' '}
                <a
                  href="https://console.amap.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  高德开放平台
                </a>
                {' '}创建应用获取
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                安全密钥 <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={securityKey}
                onChange={(e) => setSecurityKey(e.target.value)}
                placeholder="请输入安全密钥（Security Key）"
                disabled={loading}
                required
              />
              <div className="form-hint">
                在应用管理中设置密钥
              </div>
            </div>

            <div className="form-notice">
              <div className="notice-icon">ℹ️</div>
              <div className="notice-content">
                <div className="notice-title">注意事项</div>
                <ul className="notice-list">
                  <li>密钥仅保存在浏览器本地，不会上传到服务器</li>
                  <li>请确保使用的是 Web 服务 Key，而非 Web 端（JS API）Key</li>
                  <li>需要配置密钥才能使用地图和地理编码功能</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !apiKey.trim() || !securityKey.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  保存并初始化...
                </>
              ) : (
                '保存配置'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApiKeyConfig;
